import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Switch,
  Text,
} from '@chakra-ui/react';
import { CaretDown, CaretRight, MagnifyingGlass, TreeStructure } from 'phosphor-react';

import { GlassCard } from '../../../../../components/glass';
import { IEmpresaOption } from '../../../../../hooks/useContaCilImportar';
import { IPlanoContaNo, useContaCilPlanoContas } from '../../../../../hooks/useContaCilPlanoContas';
import { EmpresaPicker } from '../components/EmpresaPicker';
import { useAplicarEmpresaParam } from '../useEmpresaParam';
import {
  PeriodoRange,
  buildTodosPeriodo,
} from '../../../../analiseBots/components/PeriodoFlowFilter';
import { PeriodoSegment } from '../components/PeriodoSegment';
import { Callout, MiniKpiRow } from './ui';

const fmtValor = (v: number) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtNum = (n: number) => new Intl.NumberFormat('pt-BR').format(n);

interface NoArvore extends IPlanoContaNo {
  children: NoArvore[];
  depth: number;
}

const chaveDe = (c: IPlanoContaNo) => c.classificacao ?? c.codigo ?? '';

// Monta a árvore a partir da lista flat ordenada por classificação (o filho
// começa com a classificação do pai). Pilha de ancestrais.
function montarArvore(contas: IPlanoContaNo[]): NoArvore[] {
  const roots: NoArvore[] = [];
  const stack: NoArvore[] = [];
  contas.forEach(c => {
    const no: NoArvore = { ...c, children: [], depth: 0 };
    const cl = c.classificacao ?? '';
    while (stack.length) {
      const topo = stack[stack.length - 1].classificacao ?? '';
      if (cl.startsWith(topo) && cl.length > topo.length) break;
      stack.pop();
    }
    if (stack.length) {
      const pai = stack[stack.length - 1];
      no.depth = pai.depth + 1;
      pai.children.push(no);
    } else {
      roots.push(no);
    }
    stack.push(no);
  });
  return roots;
}

function achatar(nodes: NoArvore[], recolhidos: Set<string>, forcarAberto: boolean): NoArvore[] {
  const out: NoArvore[] = [];
  const walk = (lista: NoArvore[]) => {
    lista.forEach(n => {
      out.push(n);
      const aberto = forcarAberto || !recolhidos.has(chaveDe(n));
      if (aberto && n.children.length) walk(n.children);
    });
  };
  walk(nodes);
  return out;
}

export function ArvorePlanoEmpresa() {
  const [empresa, setEmpresa] = useState<IEmpresaOption | null>(null);
  // Aplica `?empresa=<id>` dos atalhos da pagina Empresas (uma vez, no mount).
  useAplicarEmpresaParam(setEmpresa);
  const [buscaInput, setBuscaInput] = useState('');
  const [busca, setBusca] = useState('');
  const [soComUso, setSoComUso] = useState(false);
  const [recolhidos, setRecolhidos] = useState<Set<string>>(new Set());
  // Default = todos os periodos (uso total). Filtra qtd_lancamentos por data.
  const [periodo, setPeriodo] = useState<PeriodoRange>(() => buildTodosPeriodo());

  useEffect(() => {
    const t = setTimeout(() => setBusca(buscaInput.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [buscaInput]);

  const { data, isFetching } = useContaCilPlanoContas(
    empresa?.value,
    true,
    periodo.date_init || undefined,
    periodo.date_end || undefined,
  );
  const contas = data?.contas ?? [];

  // Filtra por busca / "só com uso", mantendo os ancestrais dos matches.
  const filtradas = useMemo(() => {
    if (!busca && !soComUso) return contas;
    const bate = (c: IPlanoContaNo) =>
      (!soComUso || c.qtd_lancamentos > 0) &&
      (!busca ||
        (c.codigo ?? '').toLowerCase().includes(busca) ||
        (c.nome ?? '').toLowerCase().includes(busca) ||
        (c.classificacao ?? '').toLowerCase().includes(busca));
    const manter = new Set<string>();
    contas.forEach(c => {
      if (!bate(c)) return;
      const cl = c.classificacao ?? '';
      manter.add(chaveDe(c));
      if (cl) {
        // Mantém ancestrais (pais do match) E descendentes (filhos do match,
        // pra busca de um grupo sintético mostrar o que tem dentro).
        contas.forEach(a => {
          const acl = a.classificacao ?? '';
          if (acl && (cl.startsWith(acl) || acl.startsWith(cl))) manter.add(chaveDe(a));
        });
      }
    });
    return contas.filter(c => manter.has(chaveDe(c)));
  }, [contas, busca, soComUso]);

  const arvore = useMemo(() => montarArvore(filtradas), [filtradas]);
  const forcarAberto = !!busca || soComUso;
  const visiveis = useMemo(
    () => achatar(arvore, recolhidos, forcarAberto),
    [arvore, recolhidos, forcarAberto],
  );

  const toggle = (chave: string) =>
    setRecolhidos(prev => {
      const next = new Set(prev);
      if (next.has(chave)) next.delete(chave);
      else next.add(chave);
      return next;
    });

  const provisoriasEmUso = contas.filter(c => c.provisoria && c.qtd_lancamentos > 0).length;

  return (
    <Box>
      {/* Barra de contexto */}
      <GlassCard intensity="medium" p={4} mb={5}>
        <Flex gap={3} wrap="wrap" align={{ md: 'flex-end' }}>
          <Box flex={1} minW="240px">
            <EmpresaPicker value={empresa} onChange={setEmpresa} />
          </Box>
          <Box flex={1} minW="220px">
            <Text fontSize="10px" fontWeight="600" color="#64748B" mb={1} textTransform="uppercase">
              Buscar conta
            </Text>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <MagnifyingGlass size={14} color="#94A3B8" />
              </InputLeftElement>
              <Input
                borderRadius="lg"
                bg="white"
                placeholder="Código, nome ou classificação"
                value={buscaInput}
                onChange={e => setBuscaInput(e.target.value)}
                isDisabled={!empresa}
              />
            </InputGroup>
          </Box>
          <Flex align="center" gap={2} pb={1}>
            <Switch
              size="sm"
              colorScheme="purple"
              isChecked={soComUso}
              onChange={e => setSoComUso(e.target.checked)}
              isDisabled={!empresa}
            />
            <Text fontSize="12px" color="#64748B" fontWeight="600">
              Só com uso
            </Text>
          </Flex>
          <Box opacity={empresa ? 1 : 0.5} pointerEvents={empresa ? 'auto' : 'none'}>
            <Text fontSize="10px" fontWeight="600" color="#64748B" mb={1} textTransform="uppercase">
              Período
            </Text>
            <PeriodoSegment value={periodo} onChange={setPeriodo} />
          </Box>
        </Flex>
      </GlassCard>

      {!empresa ? (
        <GlassCard intensity="medium" py={16}>
          <Flex direction="column" align="center" gap={3}>
            <TreeStructure size={40} weight="duotone" color="#CBD5E1" />
            <Text fontSize="sm" color="#94A3B8" textAlign="center">
              Selecione uma empresa para explorar o plano de contas dela.
            </Text>
          </Flex>
        </GlassCard>
      ) : (
        <>
          {provisoriasEmUso > 0 && (
            <Callout tone="aviso" titulo="Contas a classificar com saldo">
              {provisoriasEmUso} conta(s) transitória(s)/provisória(s) têm lançamentos — devem ser
              zeradas antes de fechar a competência.
            </Callout>
          )}

          <MiniKpiRow
            kpis={[
              {
                label: 'Contas',
                valor: fmtNum(data?.total_contas ?? 0),
                icon: <TreeStructure size={18} weight="duotone" />,
                accent: '#6366F1',
              },
              {
                label: 'Analíticas',
                valor: fmtNum(data?.analiticas ?? 0),
                icon: <TreeStructure size={18} weight="duotone" />,
                accent: '#10B981',
                sub: 'recebem lançamento',
              },
              {
                label: 'Com uso',
                valor: fmtNum(data?.com_uso ?? 0),
                icon: <TreeStructure size={18} weight="duotone" />,
                accent: '#1B88F5',
                sub: 'no período importado',
              },
              {
                label: 'A classificar',
                valor: fmtNum(provisoriasEmUso),
                icon: <TreeStructure size={18} weight="duotone" />,
                accent: '#F59E0B',
                sub: 'provisórias com saldo',
              },
            ]}
          />

          <Flex align="center" justify="space-between" gap={2} mb={3} px={1} wrap="wrap">
            <Flex align="center" gap={2}>
              <Text fontSize="sm" fontWeight="600" color="#0F172A">
                {visiveis.length} conta(s) visível(eis)
              </Text>
              {isFetching && <Spinner size="xs" color="#1B88F5" thickness="2px" />}
            </Flex>
            <Flex align="center" gap={2}>
              <Button
                size="xs"
                variant="ghost"
                borderRadius="full"
                color="#64748B"
                onClick={() => setRecolhidos(new Set())}
              >
                Expandir tudo
              </Button>
              <Button
                size="xs"
                variant="ghost"
                borderRadius="full"
                color="#64748B"
                onClick={() =>
                  setRecolhidos(new Set(contas.filter(c => c.tipo === 'S').map(chaveDe)))
                }
              >
                Recolher tudo
              </Button>
            </Flex>
          </Flex>

          <GlassCard intensity="medium" p={0} overflow="hidden">
            {visiveis.length === 0 ? (
              <Box py={12}>
                <Text fontSize="sm" color="#94A3B8" textAlign="center">
                  {isFetching ? 'Carregando...' : 'Nenhuma conta para o filtro atual.'}
                </Text>
              </Box>
            ) : (
              visiveis.map(n => {
                const chave = chaveDe(n);
                const temFilhos = n.children.length > 0;
                const aberto = forcarAberto || !recolhidos.has(chave);
                const analitica = n.tipo === 'A';
                return (
                  <Flex
                    key={chave}
                    align="center"
                    gap={2}
                    px={3}
                    py={2}
                    pl={`${12 + n.depth * 18}px`}
                    borderBottomWidth="1px"
                    borderColor="rgba(15, 23, 42, 0.04)"
                    _hover={{ bg: 'rgba(27, 136, 245, 0.04)' }}
                    opacity={n.situacao === 'I' ? 0.55 : 1}
                  >
                    <Box
                      as={temFilhos ? 'button' : 'div'}
                      w="16px"
                      flexShrink={0}
                      color="#94A3B8"
                      onClick={temFilhos ? () => toggle(chave) : undefined}
                      aria-label={aberto ? 'Recolher' : 'Expandir'}
                    >
                      {temFilhos &&
                        (aberto ? (
                          <CaretDown size={13} weight="bold" />
                        ) : (
                          <CaretRight size={13} weight="bold" />
                        ))}
                    </Box>
                    <Text
                      fontFamily="mono"
                      fontSize="10px"
                      color="#94A3B8"
                      minW="84px"
                      flexShrink={0}
                    >
                      {n.classificacao ?? '—'}
                    </Text>
                    <Text
                      fontSize="13px"
                      color={analitica ? '#475569' : '#0F172A'}
                      fontWeight={analitica ? '500' : '700'}
                      flex={1}
                      minW={0}
                      isTruncated
                      title={n.nome ?? ''}
                    >
                      {n.nome ?? '—'}
                    </Text>
                    {n.provisoria && (
                      <Badge colorScheme="orange" borderRadius="full" fontSize="9px" flexShrink={0}>
                        provisória
                      </Badge>
                    )}
                    {n.inativa_em_uso && (
                      <Badge colorScheme="red" borderRadius="full" fontSize="9px" flexShrink={0}>
                        inativa em uso
                      </Badge>
                    )}
                    {n.qtd_lancamentos > 0 && (
                      <Box textAlign="right" flexShrink={0} minW="120px">
                        <Text fontSize="12px" fontWeight="600" color="#0F172A">
                          {fmtValor(n.total)}
                        </Text>
                        <Text fontSize="10px" color="#94A3B8">
                          {n.qtd_lancamentos} lanç.
                        </Text>
                      </Box>
                    )}
                    <Badge
                      flexShrink={0}
                      borderRadius="full"
                      fontSize="9px"
                      fontWeight="700"
                      px={2}
                      bg={analitica ? 'rgba(16, 185, 129, 0.10)' : 'rgba(99, 102, 241, 0.10)'}
                      color={analitica ? '#059669' : '#6366F1'}
                    >
                      {analitica ? 'A' : 'S'}
                    </Badge>
                  </Flex>
                );
              })
            )}
          </GlassCard>
        </>
      )}
    </Box>
  );
}
